import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useVisualPalette, VisualPalette } from '@/services/visualEffects';
import { useLocation } from '@/providers/LocationProvider';
import {
  ThemeMode,
  ThemeTokens,
  applyThemeTokens,
  initializeTheme,
  loadThemeMode,
  persistThemeMode,
  setTheme as setThemeInManager,
  subscribeToTheme,
  themeModeStorageKey,
  themeStorageKey,
  updateDocumentTheme,
} from './themeManager';
import { ThemeId, ThemeProfile, themeOrder, themeProfiles } from './themeProfiles';
import { inferColorScheme, lightenColor } from './colorUtils';

type AutoStatus = 'idle' | 'loading' | 'ready' | 'error';

interface ThemeContextValue {
  themeType: ThemeId;
  theme: ThemeProfile;
  availableThemes: ThemeProfile[];
  locationEnabled: boolean;
  setThemeType: (theme: ThemeId) => void;
  setLocationEnabled: (enabled: boolean) => void;
  cycleTheme: () => ThemeId;
  autoPalette: VisualPalette | null;
  autoStatus: AutoStatus;
  tokens: ThemeTokens;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const FALLBACK_TOKENS = themeProfiles.normal.tokens;

const extractLocationId = (user: unknown): string | undefined => {
  if (!user || typeof user !== 'object') {
    return undefined;
  }

  const record = user as Record<string, unknown>;
  if (typeof record['https://awkherd.app/locationId'] === 'string') {
    return record['https://awkherd.app/locationId'] as string;
  }
  if (
    typeof record.user_metadata === 'object' &&
    record.user_metadata !== null &&
    typeof (record.user_metadata as Record<string, unknown>).locationId === 'string'
  ) {
    return (record.user_metadata as Record<string, unknown>).locationId as string;
  }
  if (
    typeof record.app_metadata === 'object' &&
    record.app_metadata !== null &&
    typeof (record.app_metadata as Record<string, unknown>).locationId === 'string'
  ) {
    return (record.app_metadata as Record<string, unknown>).locationId as string;
  }
  return undefined;
};

const pickMetadataColor = (palette: VisualPalette, key: string) => {
  const value = palette.metadata?.[key];
  return typeof value === 'string' ? value : undefined;
};

const resolveVariantTokens = (
  palette: VisualPalette,
  themeType: ThemeId,
): Partial<ThemeTokens> | undefined => {
  const key = themeType === 'normal' ? 'light' : themeType;
  const meta = palette.metadata ?? {};

  const searchTargets = [
    meta[key],
    typeof meta.variants === 'object'
      ? (meta.variants as Record<string, unknown>)[key]
      : undefined,
    typeof meta.modes === 'object'
      ? (meta.modes as Record<string, unknown>)[key]
      : undefined,
  ];

  for (const target of searchTargets) {
    if (target && typeof target === 'object') {
      return target as Partial<ThemeTokens>;
    }
  }

  return undefined;
};

const derivePaletteTokens = (
  palette: VisualPalette,
  themeType: ThemeId,
): { tokens: ThemeTokens; colorScheme: 'light' | 'dark' } => {
  const variant = resolveVariantTokens(palette, themeType);
  const fallbackTokens = themeProfiles[themeType]?.tokens ?? FALLBACK_TOKENS;

  if (!variant) {
    if (themeType !== 'normal') {
      return {
        tokens: fallbackTokens,
        colorScheme: inferColorScheme(fallbackTokens.background),
      };
    }
  }

  const background =
    (variant?.background as string) ?? palette.background ?? fallbackTokens.background;
  const text = (variant?.text as string) ?? palette.text ?? fallbackTokens.text;
  const textSecondary =
    (variant?.textSecondary as string) ??
    pickMetadataColor(palette, 'textSecondary') ??
    lightenColor(text, 0.2);
  const textInverse =
    (variant?.textInverse as string) ??
    pickMetadataColor(palette, 'textInverse') ??
    background;
  const surfaceMuted =
    (variant?.surfaceMuted as string) ??
    pickMetadataColor(palette, 'surfaceMuted') ??
    lightenColor(background, 0.08);
  const primaryContrast =
    (variant?.primaryContrast as string) ??
    pickMetadataColor(palette, 'primaryContrast') ??
    textInverse;
  const secondaryContrast =
    (variant?.secondaryContrast as string) ??
    pickMetadataColor(palette, 'secondaryContrast') ??
    textInverse;

  const tokens: ThemeTokens = {
    background,
    surface:
      (variant?.surface as string) ?? pickMetadataColor(palette, 'surface') ?? background,
    surfaceMuted,
    text,
    textSecondary,
    textInverse,
    primary: (variant?.primary as string) ?? palette.primary ?? fallbackTokens.primary,
    primaryContrast,
    secondary:
      (variant?.secondary as string) ?? palette.secondary ?? fallbackTokens.secondary,
    secondaryContrast,
    accent: (variant?.accent as string) ?? palette.accent ?? fallbackTokens.accent,
  };

  const colorSchemeMetadata = pickMetadataColor(palette, 'colorScheme');
  const colorScheme =
    colorSchemeMetadata === 'dark' || colorSchemeMetadata === 'light'
      ? colorSchemeMetadata
      : inferColorScheme(tokens.background);

  return { tokens, colorScheme };
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const initialTheme = useMemo(() => initializeTheme(), []);
  const initialMode = useMemo(() => loadThemeMode(), []);

  const [themeType, setThemeTypeState] = useState<ThemeId>(initialTheme);
  const [locationEnabled, setLocationEnabledState] = useState<boolean>(
    initialMode === 'auto',
  );
  const [activeTokens, setActiveTokens] = useState<ThemeTokens>(
    themeProfiles[initialTheme].tokens,
  );
  const [lastPalette, setLastPalette] = useState<VisualPalette | null>(null);

  const { user } = useAuth();
  const locationId = extractLocationId(user);

  const geolocation = useLocation();

  const paletteQuery = useVisualPalette({
    lat: geolocation.coords?.latitude,
    lng: geolocation.coords?.longitude,
    locationId,
    preferredTheme: themeType,
    enabled:
      locationEnabled &&
      (Boolean(geolocation.coords) ||
        geolocation.status === 'denied' ||
        geolocation.status === 'unsupported' ||
        Boolean(locationId)),
  });

  const updateTokens = useCallback(
    (tokens: ThemeTokens, dataset: ThemeId | 'auto', colorScheme?: 'light' | 'dark') => {
      setActiveTokens(tokens);
      applyThemeTokens(tokens);
      updateDocumentTheme(dataset, colorScheme ?? inferColorScheme(tokens.background));
    },
    [],
  );

  const persistLocationPreference = useCallback((enabled: boolean) => {
    persistThemeMode(enabled ? 'auto' : 'manual');
    setLocationEnabledState(enabled);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToTheme((nextTheme) => {
      setThemeTypeState(nextTheme);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === themeStorageKey && typeof event.newValue === 'string') {
        if (event.newValue in themeProfiles) {
          setThemeTypeState(event.newValue as ThemeId);
        }
      }
      if (
        event.key === themeModeStorageKey &&
        (event.newValue === 'auto' || event.newValue === 'manual')
      ) {
        setLocationEnabledState(event.newValue === 'auto');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (!locationEnabled) {
      const { tokens } = themeProfiles[themeType];
      updateTokens(tokens, themeType);
      setLastPalette(null);
    }
  }, [locationEnabled, themeType, updateTokens]);

  useEffect(() => {
    if (!locationEnabled) {
      return;
    }

    if (paletteQuery.data) {
      const { tokens, colorScheme } = derivePaletteTokens(paletteQuery.data, themeType);
      setLastPalette(paletteQuery.data);
      updateTokens(tokens, themeType, colorScheme);
    } else if (paletteQuery.isError) {
      setLastPalette(null);
      const { tokens } = themeProfiles[themeType];
      updateTokens(tokens, themeType);
    }
  }, [locationEnabled, paletteQuery.data, paletteQuery.isError, themeType, updateTokens]);

  const setTheme = useCallback((nextTheme: ThemeId) => {
    setThemeInManager(nextTheme);
    setThemeTypeState(nextTheme);
  }, []);

  const cycleTheme = useCallback(() => {
    const currentIndex = themeOrder.indexOf(themeType);
    const nextTheme = themeOrder[(currentIndex + 1) % themeOrder.length];
    setTheme(nextTheme);
    return nextTheme;
  }, [setTheme, themeType]);

  const availableThemes = useMemo(() => themeOrder.map((id) => themeProfiles[id]), []);

  const autoStatus: AutoStatus = useMemo(() => {
    if (!locationEnabled) {
      return 'idle';
    }
    if (paletteQuery.isError) {
      return 'error';
    }
    if (paletteQuery.isLoading) {
      return 'loading';
    }
    return paletteQuery.data ? 'ready' : 'loading';
  }, [locationEnabled, paletteQuery.data, paletteQuery.isError, paletteQuery.isLoading]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      themeType,
      theme: themeProfiles[themeType],
      availableThemes,
      locationEnabled,
      setThemeType: setTheme,
      setLocationEnabled: persistLocationPreference,
      cycleTheme,
      autoPalette: lastPalette,
      autoStatus,
      tokens: activeTokens,
    }),
    [
      activeTokens,
      availableThemes,
      autoStatus,
      lastPalette,
      locationEnabled,
      persistLocationPreference,
      setTheme,
      cycleTheme,
      themeType,
    ],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used inside a ThemeProvider');
  }
  return context;
};
